import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { AuthButton } from '@/core/permissions/AuthButton';
import { Plus } from 'lucide-react';
import { CategoryTableBlock } from './components/CategoryTableBlock';
import { CategoryTabs } from './components/CategoryTabs';
import { CategoryToolbar } from './components/CategoryToolbar';
import { useCategoriesList } from './hooks/useCategoriesList';
import { useSyncCategories } from './hooks/useSyncCategories';
import { RefreshCw } from 'lucide-react';
import type { SettingsCategory } from './types/categoriesList.types';

const pageSize = 10;

export function CategoriesListPage() {
  const navigate = useNavigate();
  const {
    categories,
    filteredCategories,
    selectedCategory,
    filters,
    summary,
    isLoading,
    setFilters,
    setSelectedId,
    deleteCategory,
  } = useCategoriesList();
  
  const { syncCategories, isSyncing } = useSyncCategories();

  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; category: SettingsCategory | null }>({
    open: false,
    category: null,
  });

  const totalParentItems = useMemo(
    () => filteredCategories.filter((item) => !item.parentId).length,
    [filteredCategories],
  );

  return (
    <div className="space-y-4">
      
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white p-3 rounded-md border border-slate-100 shadow-sm">
        <div className="min-w-0 flex-1 overflow-x-auto">
          <CategoryTabs 
            activeTab={filters.type} 
            onChange={(value) => setFilters({ ...filters, type: value })} 
            onAdd={() => navigate('?tab=categories-new')}
          />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <AuthButton
            module="settings.categories"
            action="create"
            onClick={syncCategories}
            isLoading={isSyncing}
            className="h-9 rounded-md bg-blue-50 px-4 text-xs md:text-sm text-blue-700 shadow-sm hover:bg-blue-100 flex items-center gap-2 border border-blue-200"
          >
            <RefreshCw className={isSyncing ? "w-4 h-4 animate-spin" : "w-4 h-4"} />
            <span>Đồng bộ Mẫu</span>
          </AuthButton>
          <AuthButton
            module="settings.categories"
            action="create"
            behavior="disable"
            permissionReason="Bạn không có quyền thêm danh mục mới"
            onClick={() => navigate('?tab=categories-new')}
            className="h-9 rounded-md bg-emerald-600 px-4 text-xs md:text-sm text-white shadow-sm hover:bg-emerald-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Thêm mới</span>
          </AuthButton>
        </div>
      </div>

      <CategoryToolbar filters={filters} onChange={setFilters} />
      <CategoryTableBlock
        categories={filteredCategories}
        selectedCategoryId={selectedCategory?.id || null}
        isLoading={isLoading}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalParentItems}
        onPageChange={setCurrentPage}
        onSelect={(category) => setSelectedId(category.id)}
        onEdit={(category) => navigate(`?tab=categories-edit&id=${category.id}`)}
        onDuplicate={(category) => navigate(`?tab=categories-new&duplicateId=${category.id}`)}
        onDelete={(category) => setDeleteModal({ open: true, category })}
        onViewDetail={(category) => navigate(`?tab=categories-detail&id=${category.id}`)}
      />

      <ConfirmDialog
        isOpen={deleteModal.open}
        title="Xác nhận xóa danh mục"
        message={`Bạn có chắc chắn muốn xóa danh mục "${deleteModal.category?.name || ''}"?`}
        confirmText="Xóa"
        cancelText="Hủy"
        isDestructive
        onConfirm={() => {
          if (deleteModal.category) deleteCategory(deleteModal.category.id);
          setDeleteModal({ open: false, category: null });
        }}
        onClose={() => setDeleteModal({ open: false, category: null })}
      />
    </div>
  );
}

